import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { IMAGES } from "./data";

const LINES = ["Precision", "Tiling for", "Ireland's", "finest spaces"];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.3 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.42, 0.68]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.heroTexture}
          alt="Luxury marble bathroom by MG Tiling Pro"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-[1] bg-ink"
      />
      <div className="absolute inset-0 z-[1] bg-gradient-to-r from-ink via-ink/40 to-transparent" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-ink via-transparent to-ink/50" />

      <div className="relative z-10 mx-auto max-w-[1600px] w-full px-6 md:px-10 pb-16 md:pb-24 pt-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="font-heading text-xs md:text-sm uppercase tracking-[0.3em] font-bold text-terracotta mb-8"
        >
          [ Est. Dublin ] — Master Tilers of Ireland
        </motion.p>

        <h1 className="font-heading font-black uppercase text-bone tracking-tighter leading-[0.82] text-[16vw] sm:text-[13vw] lg:text-[9.5vw]">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="show"
                className={`block ${i === 3 ? "text-stroke-accent" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8 border-t border-line pt-8"
        >
          <p className="max-w-md font-body font-light text-base md:text-lg text-stone leading-relaxed">
            A specialist tiling studio delivering architect-grade bathrooms, kitchens
            and wet rooms across Leinster and beyond.
          </p>
          <a
            href="#contact"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 font-heading text-xs uppercase tracking-[0.2em] font-bold bg-bone text-ink px-8 py-4 hover:bg-terracotta hover:text-bone transition-colors duration-300 self-start"
          >
            Request a Quote
            <ArrowDownRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1"
            />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
